import moment from 'moment';
import { mergeFrom, deepClone } from 'src/helpers/object';

function findAppointment(state, data) {
  const main = state.items.find(({ key }) => key === data.key);
  if (main) {
    const refs = state.items.filter(({ refKey }) => refKey && refKey === data.key);
    return { main, refs };
  }
  return null;
}

function clearAppointment(state, data) {
  const found = findAppointment(state, data);
  if (found) {
    const { main, refs } = found;
    [main, ...refs].forEach((item) => {
      if (item.isSlot || item.isArrivalOrder) {
        state.items.splice(state.items.indexOf(item), 1);
      } else {
        item.id = null;
        item.patient = null;
        item.insurance = null;
        item.status = 'available';
        item.expenses = [];
        item.surgery = null;
        item.refKey = null;
      }
    });
  }
}

function restorePreviousClipboardIntention(state) {
  if (state.clipboard.data !== null && state.clipboard.event === 'cut') {
    const original = findAppointment(state, state.clipboard.data);
    if (original) {
      Object.assign(original.main, state.clipboard.data);
    }
  }
}

export default {
  'SurgeryAppointment.SET_ITEMS': function setItems(state, data) {
    state.items = data;
  },
  'SurgeryAppointment.COPY_APPOINTMENT': function copyAppointment(state, data) {
    restorePreviousClipboardIntention(state);
    state.clipboard.event = 'copy';
    state.clipboard.data = deepClone(data);
    if (Array.isArray(state.clipboard.data.expenses)) {
      state.clipboard.data.expenses = state.clipboard.data.expenses.map((item) => {
        item.accountId = null;
        return item;
      });
    }
  },
  'SurgeryAppointment.CUT_APPOINTMENT': function cutAppointment(state, data) {
    restorePreviousClipboardIntention(state);
    state.clipboard.event = 'cut';
    state.clipboard.data = deepClone(data);
    clearAppointment(state, data);
  },
  'SurgeryAppointment.UPSERT': function upsert(state, data) {
    if ('originalKey' in data && data.originalKey) {
      clearAppointment(state, { ...data, key: data.originalKey });
    }

    if (!moment(data.startDate).isSame(state.filter.date, 'day')) return;
    if (state.filter.scheduleId && state.filter.scheduleId !== data.scheduleId) return;
    if (state.filter.insuranceId) {
      if (!state.insurances.some(({ id }) => data.insurance.id === id)) return;
    }

    const found = findAppointment(state, data);
    if (found) {
      data.available.insurancePlans = found.main.available.insurancePlans;
      Object.assign(found.main, data);
    } else if (
      state.items.length === 0
      || state.items[state.items.length - 1].startDate < data.startDate
    ) {
      state.items.push(data);
    } else if (state.items[0].startDate > data.startDate) {
      state.items.unshift(data);
    } else {
      let idx = 0;

      state.items.forEach((item, i) => {
        if (data.startDate > item.startDate) {
          idx = i + 1;
        }
      });

      state.items.splice(idx, 0, data);
    }
  },
  'SurgeryAppointment.REMOVE': function upsert(state, data) {
    clearAppointment(state, data);
  },
  'SurgeryAppointment.SET_SCHEDULES': function setSchedules(state, data) {
    state.schedules = data;
  },
  'SurgeryAppointment.SET_PROFESSIONALS': function setProfessionals(state, data) {
    state.professionals = data;
  },
  'SurgeryAppointment.SET_INSURANCES': function setInsurances(state, data) {
    state.insurances = data;
  },

  'SurgeryAppointment.UPDATE_AVAILABLE_DATA': function updateAvailableData(state) {
    const { scheduleId } = state.form;
    const blankData = state.blankForm();
    state.available.professionals = state.professionals
      .filter(({ schedules }) => schedules.includes(scheduleId));
    state.available.insurancePlans = state.insurances
      .filter(({ schedules }) => schedules.includes(scheduleId))
      .map(({ plan }) => plan.id);
    state.form.professional = blankData.professional;
    state.form.insurance = blankData.insurance;
  },
  'SurgeryAppointment.SHOW_APPOINTMENT_MODAL': function showAppointmentModal(state, data = null) {
    if (data) {
      data = deepClone(data);
      data.expenses = data.expenses || [];
      state.available.professionals = data.available.professionals;
      state.available.insurancePlans = data.available.insurancePlans;
      state.form = mergeFrom(state.form, state.blankForm());
      state.form = mergeFrom(state.form, data);
      if (data.available.professionals.length === 1) {
        const [professional] = data.available.professionals;
        if (professional.specialties.length === 1) {
          state.form.professional.id = professional.id;
          [state.form.professional.specialty] = professional.specialties;
        }
      }
      state.form.status = data.status === 'available' ? 'scheduled' : data.status;
      state.form.slot = data.slot || data.isSlot;
    } else {
      state.form = mergeFrom(state.form, state.blankForm());
      state.form.startDate = moment().format('YYYY-MM-DD[T]HH:mm');
      if (state.form.startDate.substring(0, 10) !== state.filter.date) {
        state.form.startDate = `${state.filter.date}T08:00`;
      }
      state.form.scheduleId = state.schedules.length > 0 ? state.schedules[0].id : null;
      state.form.slot = true;
      state.available.professionals = [];
      state.available.insurancePlans = [];
    }
    state.showFiles = false;
    state.showHistory = false;
    state.show = true;
  },
  'SurgeryAppointment.HIDE_APPOINTMENT_MODAL': function hideAppointmentModal(state) {
    // eslint-disable-next-line
    state.show = false;
  },
  'SurgeryAppointment.SHOW_ACCOUNT_MODAL': function showAccountModal(state) {
    // eslint-disable-next-line
    state.accountShow = true;
  },
  'SurgeryAppointment.HIDE_ACCOUNT_MODAL': function hideAccountModal(state) {
    // eslint-disable-next-line
    state.accountShow = false;
  },
  'SurgeryAppointment.SHOW_SLOT_MODAL': function showSlotModal(state) {
    state.slotShow = true;
  },
  'SurgeryAppointment.HIDE_SLOT_MODAL': function hideSlotModal(state) {
    state.slotShow = false;
  },
  'SurgeryAppointment.SHOW_SEARCH_APPOINTMENT_MODAL': function showSearchAppointmentModal(state) {
    // eslint-disable-next-line
    state.searchShow = true;
  },
  'SurgeryAppointment.HIDE_SEARCH_APPOINTMENT_MODAL': function hideSearchAppointmentModal(state) {
    // eslint-disable-next-line
    state.searchShow = false;
  },
  'SurgeryAppointment.SHOW_PRINT_APPOINTMENT_MODAL': function showPrintAppointmentModal(state) {
    // eslint-disable-next-line
    state.printShow = true;
  },
  'SurgeryAppointment.HIDE_PRINT_APPOINTMENT_MODAL': function hidePrintAppointmentModal(state) {
    // eslint-disable-next-line
    state.printShow = false;
  },
  'SurgeryAppointment.SET_LOADING': function setLoading(state, value) {
    state.loading = value;
  },
  'SurgeryAppointment.SET_APPOINTMENTS': function setAppointments(state, data) {
    state.appointments = data;
  },
  'SurgeryAppointment.ADD_EXPENSE': function addExpense(state, data) {
    state.form.expenses.push(data);
  },
  'SurgeryAppointment.DELETE_EXPENSE': function addDeleteExpense(state, data) {
    const account = state.form.expenses
      .find(item => item.id === data.id && item.accountId === null);
    if (account) {
      const index = state.form.expenses.indexOf(account);
      state.form.expenses.splice(index, 1);
    }
  },
  'SurgeryAppointment.CLEAR_CLIPBOARD': function clearClipboard(state) {
    state.clipboard.event = null;
    state.clipboard.data = null;
  },
  'SurgeryAppointment.SET_ACCOUNT': function setAccount(state, data) {
    const found = findAppointment(state, data);

    if (found) {
      found.main.expenses = data.expenses;
    }
  },
  'SurgeryAppointment.SET_CURRENT_MONTH': function setCurrentMonth(state, date) {
    state.currentMonth = date;
  },
};
